import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, ShoppingCart } from 'lucide-react';
import {
  coverOf,
  formatPrice,
  pickHeroProjects,
  productPath,
} from '../productHelpers';
import './HeroSection4.css';

// Facts that hold for every project in the catalog.
const BUNDLE = [
  'Complete source code',
  'Project documentation',
  'UPI / QR checkout',
  'Custom changes on request',
];

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const Thumb = ({ product }) => {
  const [failed, setFailed] = useState(false);
  const src = coverOf(product);

  if (!src || failed) {
    return (
      <span className="mh-tile__thumb mh-tile__thumb--empty" aria-hidden="true">
        {(product.name || '?').charAt(0)}
      </span>
    );
  }

  return (
    <img
      className="mh-tile__thumb"
      src={src}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
    />
  );
};

const LeadTile = ({ product }) => {
  const stack = (product.technologies || []).slice(0, 4);

  return (
    <article className="mh-tile mh-tile--lead">
      <div className="mh-lead__body">
        <h2 className="mh-lead__name">
          <Link to={productPath(product)}>{product.name}</Link>
        </h2>
        <p className="mh-lead__meta">
          {[product.category, product.isFeatured && 'Featured project']
            .filter(Boolean)
            .join(' · ')}
        </p>
        {product.sortDescription && (
          <p className="mh-lead__desc">{product.sortDescription}</p>
        )}
        {stack.length > 0 && (
          <p className="mh-lead__stack">{stack.join(' · ')}</p>
        )}
      </div>

      <div className="mh-lead__foot">
        <span className="mh-lead__price">{formatPrice(product.price)}</span>
        <div className="mh-lead__actions">
          <Link className="mh-pill mh-pill--light" to={`/checkout/${product.id}`}>
            <ShoppingCart aria-hidden="true" />
            {Number(product.price) === 0 ? 'Get it free' : 'Buy now'}
          </Link>
          <Link className="mh-pill mh-pill--ghost" to={productPath(product)}>
            Details
          </Link>
        </div>
      </div>
    </article>
  );
};

const ProductTile = ({ product, area }) => (
  <Link
    className={`mh-tile mh-tile--product mh-area-${area}`}
    to={productPath(product)}
  >
    <span className="mh-tile__top">
      <Thumb product={product} />
      <ArrowUpRight className="mh-tile__arrow" aria-hidden="true" />
    </span>
    <span className="mh-tile__name">{product.name}</span>
    <span className="mh-tile__foot">
      <span className="mh-tile__category">{product.category}</span>
      <span className="mh-tile__price">{formatPrice(product.price)}</span>
    </span>
  </Link>
);

const HeroSection4 = ({ products = [], totalCount, isLoading = false }) => {
  const projects = useMemo(() => pickHeroProjects(products), [products]);
  const categoryCount = useMemo(
    () => new Set(products.map(p => p?.category).filter(Boolean)).size,
    [products]
  );
  const projectCount = Math.max(Number(totalCount) || 0, products.length);
  const [settled, setSettled] = useState(false);
  const seenLoading = useRef(false);

  // The first render happens before the fetch starts, so "not loading" alone
  // doesn't mean the catalog came back empty.
  useEffect(() => {
    if (isLoading) seenLoading.current = true;
    else if (seenLoading.current) setSettled(true);
  }, [isLoading]);

  const showSkeleton = !projects.length && !settled;
  const [lead, ...rest] = projects;

  return (
    <section className="mh-hero" aria-labelledby="mh-hero-title">
      <div className="mh-hero__inner">
        <h1 id="mh-hero-title" className="mh-hero__title">
          Real-world software,{' '}
          <span className="mh-hero__soft">ready to deploy.</span>
        </h1>

        <p className="mh-hero__lede">
          Complete source code for mobile apps, web platforms, admin panels and
          APIs, documented and ready to ship under your own brand.
        </p>

        <div className="mh-hero__actions">
          <Link className="mh-btn mh-btn--primary" to="/products">
            Browse projects
            <ArrowRight aria-hidden="true" className="mh-btn__arrow" />
          </Link>
          <Link className="mh-btn mh-btn--secondary" to="/request-project">
            Request a custom build
          </Link>
        </div>

        {(showSkeleton || lead) && (
          <div className="mh-bento" aria-busy={showSkeleton || undefined}>
            {showSkeleton ? (
              <>
                <div className="mh-tile mh-tile--skeleton mh-area-lead" />
                <div className="mh-tile mh-tile--skeleton mh-area-stats" />
                {['p1', 'p2', 'p3', 'p4'].map(area => (
                  <div
                    key={area}
                    className={`mh-tile mh-tile--skeleton mh-area-${area}`}
                  />
                ))}
              </>
            ) : (
              <>
                <LeadTile product={lead} />

                <Link className="mh-tile mh-tile--stats" to="/products">
                  <span className="mh-stats__count">
                    {plural(projectCount, 'project')}
                  </span>
                  <span className="mh-stats__label">
                    across {categoryCount}{' '}
                    {categoryCount === 1 ? 'category' : 'categories'}
                  </span>
                  <span className="mh-stats__link">
                    Browse the catalog
                    <ArrowRight aria-hidden="true" />
                  </span>
                </Link>

                {rest.map((product, i) => (
                  <ProductTile
                    key={product.id}
                    product={product}
                    area={`p${i + 1}`}
                  />
                ))}
              </>
            )}

            <div className="mh-tile mh-tile--bundle">
              <h2 className="mh-bundle__title">Every bundle includes</h2>
              <ul className="mh-bundle__list">
                {BUNDLE.map(item => (
                  <li key={item}>
                    <Check aria-hidden="true" strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Link className="mh-tile mh-tile--custom" to="/request-project">
              <span className="mh-custom__title">Need something specific?</span>
              <span className="mh-custom__link">
                Request a custom build
                <ArrowRight aria-hidden="true" />
              </span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroSection4;
